/// <amd-module name="Repos/Views/Settings/Repositories/Components/RepositoriesMasterDetails" />
import React = require("react");
import { GitRepositoryPermissions } from "Repos/Common/Permissions";
import { ISettingsViewService } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { RepositoriesDetailsPanel } from "Repos/Views/Settings/Repositories/Components/RepositoriesDetailsPanel";
import { RepositoriesMasterPanel } from "Repos/Views/Settings/Repositories/Components/RepositoriesMasterPanel";
import { IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories/Resources";
import { IRepositoriesViewService } from "Repos/Views/Settings/Repositories/Services/RepositoriesViewService";
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { DetailsPanel, MasterPanel, MasterPanelHeader } from "VSSUI/MasterDetails";
import {
    BaseMasterDetailsContext,
    createChildLayer,
    IDetailsAreaContent,
    IMasterDetailsContext,
    IMasterDetailsContextLayer,
    IMasterPanelContent,
    MasterDetailsContext
} from "VSSUI/MasterDetailsContext";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, IFilter } from "VSSUI/Utilities/Filter";
import { RefsMasterPanel } from "./RefsMasterPanel";

interface IRepositoriesMasterDetailsProps {
    renderCreatePanel(): void;
}

export class RepositoriesMasterDetails extends VssComponent<IRepositoriesMasterDetailsProps> {
    private commandBarItems: IHeaderCommandBarItem[];
    private filter: IFilter;
    private masterDetailsContext: IMasterDetailsContext;
    private masterDetailsContextLayer: IMasterDetailsContextLayer<IRepository, undefined>;
    private masterDetailsSelected: IObservableValue<IRepository>;
    private selected: IReadonlyObservableValue<IRepository | undefined>;
    private selectedRef: IReadonlyObservableValue<string | undefined>;
    private settingsService: ISettingsViewService;
    private svc: IRepositoriesViewService;

    public constructor(props: IRepositoriesMasterDetailsProps, context: IVssComponentContext) {
        super(props, context);
        this.svc = context.pageContext.getService<IRepositoriesViewService>("RepositoriesViewService");
        this.selected = this.svc.getSelected();
        this.selectedRef = this.svc.getSelectedRefs();
        this.filter = this.svc.getFilter();
        this.masterDetailsSelected = new ObservableValue<IRepository>(this.selected.value as IRepository);
        this.setCommandBarItems();
        this.settingsService = context.pageContext.getService<ISettingsViewService>("SettingsViewService");

        const detailsAreaContent: IDetailsAreaContent<IRepository> = {
            renderContent: item => <RepositoriesDetailsPanel data={item} createChildLayer={this.createChildLayer}></RepositoriesDetailsPanel>
        };

        this.masterDetailsContextLayer = {
            key: "repositoriesMasterDetails",
            detailsContent: detailsAreaContent,
            masterPanelContent: {
                renderContent: (parentItem, selectedItem) => <RepositoriesMasterPanel selected={selectedItem} />,
                renderHeader: () => {
                    return (
                        <Header
                            backButtonProps={{
                                onClick: this.onBackClick,
                                subtle: true,
                                href: this.settingsService.getUrl()
                            }}
                            commandBarItems={this.commandBarItems}
                            className="bolt-master-panel-standard-header"
                            descriptionClassName="bolt-master-panel-header-subtitle bolt-master-panel-header-secondary"
                            title={Resources.AllRepositories}
                            titleClassName="bolt-master-panel-header-title"
                            titleSize={TitleSize.Large}
                        />
                    );
                },
                renderSearch: () => {
                    return <InlineKeywordFilterBarItem filter={this.filter} filterItemKey="name" />;
                }
            },
            selectedMasterItem: this.masterDetailsSelected,
            parentItem: undefined
        };

        this.masterDetailsContext = new BaseMasterDetailsContext(this.masterDetailsContextLayer, this.onBackClick);
        this.createChildLayer();
    }

    public componentDidMount = () => {
        this.selected.subscribe(this.syncSelected);
        this.selectedRef.subscribe(this.syncSelected);
    };

    public componentWillUnmount = () => {
        this.selected.unsubscribe(this.syncSelected);
        this.selectedRef.unsubscribe(this.syncSelected);
    };

    public render = (): JSX.Element => {
        return (
            <MasterDetailsContext.Provider value={this.masterDetailsContext}>
                <div className="flex-row flex-grow v-scroll-auto">
                    <MasterPanel />
                    <DetailsPanel />
                </div>
            </MasterDetailsContext.Provider>
        );
    };

    private createChildLayer = () => {
        const currentLayer = this.masterDetailsContext.getCurrentLayer().value;
        const pushed = currentLayer && currentLayer.key.startsWith(refMasterPanelKeyPrefix);
        if (!pushed) {
            const key = refMasterPanelKeyPrefix + this.svc.getStateKey();
            if (this.svc.getSelectedRefs().value) {
                const filter = new Filter();
                const childMasterPanelContent: IMasterPanelContent<string, IRepository> = {
                    renderContent: (parentItem, selectedItem) => <RefsMasterPanel selected={selectedItem} filter={filter} />,
                    renderHeader: () => {
                        return (
                            <Header
                                backButtonProps={{
                                    onClick: this.onBackClickChild,
                                    subtle: true,
                                    href: this.settingsService.getUrl(this.selected.value ? this.selected.value.id : undefined, undefined, undefined)
                                }}
                                commandBarItems={this.commandBarItems}
                                className="bolt-master-panel-standard-header"
                                descriptionClassName="bolt-master-panel-header-subtitle bolt-master-panel-header-secondary"
                                title={this.selected.value ? this.selected.value.name : Resources.AllRepositories}
                                titleClassName="bolt-master-panel-header-title"
                                titleSize={TitleSize.Large}
                            />
                        );
                    },
                    renderSearch: () => {
                        return <InlineKeywordFilterBarItem filter={filter} filterItemKey="text-search" />;
                    }
                };

                const childDetailsAreaContent: IDetailsAreaContent<string> = {
                    renderContent: item => <RepositoriesDetailsPanel data={item} createChildLayer={this.createChildLayer}></RepositoriesDetailsPanel>
                };

                const newContext = createChildLayer(
                    key,
                    childMasterPanelContent,
                    childDetailsAreaContent,
                    this.svc.getSelectedRefs().value,
                    this.masterDetailsContextLayer
                );
                this.masterDetailsContext.push(newContext);
            }
        }
    };

    private setCommandBarItems = () => {
        if (this.svc.checkPermissions(GitRepositoryPermissions.CreateRepository)) {
            this.commandBarItems = [
                {
                    ariaLabel: Resources.CreateLabel,
                    iconProps: { iconName: "Add" },
                    id: "add-repository",
                    onActivate: () => {
                        this.props.renderCreatePanel();
                    },
                    subtle: true,
                    text: "",
                    tooltipProps: {
                        text: Resources.CreateLabel
                    }
                }
            ];
        }
    };

    private onBackClick = (event?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
        if (!event || (!event.shiftKey && !event.ctrlKey)) {
            this.svc.setSelected(undefined);
            if (event) {
                event.preventDefault();
            }
        }
    };

    private onBackClickChild = (event?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
        if (!event || (!event.shiftKey && !event.ctrlKey)) {
            this.svc.setSelectedRefs(undefined);

            if (event) {
                event.preventDefault();
            }
        }
    };

    private syncSelected = () => {
        if (this.selected.value && this.selected.value != this.masterDetailsSelected.value) {
            this.masterDetailsSelected.value = this.selected.value;
        }

        if (!this.selectedRef.value) {
            const currentLayer = this.masterDetailsContext.getCurrentLayer().value;
            const pop = currentLayer && currentLayer.key.startsWith(refMasterPanelKeyPrefix);
            if (pop) {
                this.masterDetailsContext.pop();
            }
        }

        if (this.selectedRef.value) {
            const currentLayer = this.masterDetailsContext.getCurrentLayer().value;
            const pushed = currentLayer && currentLayer.key.startsWith(refMasterPanelKeyPrefix);
            if (!pushed) {
                this.createChildLayer();
            }
        }
    };
}

const refMasterPanelKeyPrefix = "refLayer/";
